/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Map } from 'immutable';
import { compose } from 'redux';
import get from 'lodash/get';

import { relativeContextItemsByHitSetKey as relativeContextItemsByHitSetKeySelector } from '@Containers/RelativeContext/RelativeContextSelectors';
import {
  getRelativeContexts as getRelativeContextsAction,
  goToResultPage as goToResultPageAction,
  openExportDialog as openExportDialogAction,
} from '@Pages/Results/actions';
import withActiveBreadcrumb from '@Pages/Results/HOCs/withActiveBreadcrumb';
import useAvailableDatabases from '@Pages/Results/hooks/useAvailableDatabases';

import {
  HeatmapTooltipHeadersHoverRows,
  HeatmapTooltipHeadersHoverCols,
  HeatmapTooltipHeadersOptionsRows,
  HeatmapTooltipHeadersOptionsCols,
} from '@Pages/Heatmap/HeatmapTooltipHeaders';

import {
  toggleFullScreenMode as toggleFullScreenModeAction,
  toggleNavigator as toggleNavigatorAction,
  toggleLegend as toggleLegendAction,
  showResultsList as showResultsListAction,
  limitTo as limitToAction,
  fetchHeatmapData as fetchHeatmapDataAction,
  onMatrixReceived as onMatrixReceivedAction,
  onInitData as onInitDataAction,
  onRequestData as onRequestDataAction,
  onSelectionChange as onSelectionChangeAction,
  onUnmount as onUnmountAction,
  selectDatabase as selectDatabaseAction,
} from '@Pages/Heatmap/actions';
import { useHistory } from 'react-router-dom';
import { withCombinedRouteParamsforHeatmapResults } from '@AppRoutes/ConnectedRoute';
import {
  open as openSelector,
  settings as settingsSelector,
  fullScreenMode as fullScreenModeSelector,
  heatmapDataForParams as heatmapDataForParamsSelector,
  matrixReceivedForParams as matrixReceivedForParamsSelector,
  isLimitDisabled as isLimitDisabledSelector,
} from './selectors';

import useFullScreenMode from './hooks/useFullScreenMode';
import { trackHeatmap } from './analytics';
import Heatmap from './Heatmap';

export function HeatmapContainer({
  params,
  activeBreadcrumb,
  relativeContexts,
  getRelativeContexts,
  selectDatabase,
  goToResultPage,
  isExportEnabled,
  openExportDialog,

  // Heatmap specific
  open,
  settings,
  fullScreenMode,
  toggleFullScreenMode,
  toggleNavigator,
  toggleLegend,
  showResultsList,
  isLimitDisabled,
  limitTo,
  fetchHeatmapData,
  heatmapData,
  matrixReceived,
  onMatrixReceived,
  onInitData,
  onRequestData,
  onSelectionChange,
  onUnmount,
}) {
  const { fromContext } = params;
  const history = useHistory();

  useEffect(() => {
    if (!relativeContexts) {
      getRelativeContexts(params);
    }
  }, [relativeContexts]);

  useEffect(() => {
    const skipLinkFooter = document.getElementById('els-skiplink-footer');
    const footerListItem = skipLinkFooter ? skipLinkFooter.parentElement : null;
    if (footerListItem) {
      footerListItem.style.display = 'none'; // Hide the element that contains the skip link for footer
    }
    return () => {
      if (footerListItem) {
        footerListItem.style.display = ''; // Reset display to show the footer element again
      }
    };
  }, []);

  const { availableDatabases, currentDatabase, isDbSwitcherVisible } = useAvailableDatabases(params, activeBreadcrumb);

  useFullScreenMode(open, fullScreenMode);

  useEffect(() => {
    if (!heatmapData && history.location?.pathname?.includes('heatmap')) {
      fetchHeatmapData(params, activeBreadcrumb);
    }
  }, [params]);

  useEffect(() => {
    if (matrixReceived) {
      onMatrixReceived(params);

      // AA
      trackHeatmap({
        params,
        heatmapData,
        activeBreadcrumb,
        settings,
      });
    }
  }, [matrixReceived, params]);

  useEffect(
    () => () => {
      onUnmount();
    },
    [params]
  );

  const goToResultPageCallback = useCallback((newParams) => goToResultPage(newParams, params), [params]);

  return (
    <>
      {heatmapData && (
        <>
          <HeatmapTooltipHeadersHoverRows dataMap={get(heatmapData, 'headers.rows')} type={get(settings, 'row.value')} />
          <HeatmapTooltipHeadersHoverCols dataMap={get(heatmapData, 'headers.cols')} type={get(settings, 'col.value')} />
          <HeatmapTooltipHeadersOptionsRows params={params} dataMap={get(heatmapData, 'headers.rows')} type={get(settings, 'row.value')} />
          <HeatmapTooltipHeadersOptionsCols params={params} dataMap={get(heatmapData, 'headers.cols')} type={get(settings, 'col.value')} />
        </>
      )}
      <Heatmap
        fromContext={fromContext}
        isDbSwitcherVisible={isDbSwitcherVisible}
        availableDatabases={availableDatabases}
        currentDatabase={currentDatabase}
        selectDatabase={selectDatabase}
        showResultsList={() => showResultsList(params, relativeContexts)}
        toggleFullScreenMode={toggleFullScreenMode}
        toggleLegend={toggleLegend}
        toggleNavigator={toggleNavigator}
        isLimitDisabled={isLimitDisabled}
        limitTo={(options) => limitTo(options, params)}
        isExportEnabled={isExportEnabled}
        openExportDialog={(tpd) => openExportDialog(tpd, params, { relativeContexts })}
        goToResultPage={goToResultPageCallback}
        heatmapData={heatmapData}
        heatmapSettings={settings}
        onInitData={onInitData}
        onRequestData={onRequestData}
        onSelectionChange={onSelectionChange}
      />
    </>
  );
}

HeatmapContainer.propTypes = {
  params: PropTypes.object.isRequired,
  activeBreadcrumb: PropTypes.object.isRequired, // from HOC
  relativeContexts: PropTypes.instanceOf(Map),
  getRelativeContexts: PropTypes.func.isRequired,
  selectDatabase: PropTypes.func.isRequired,
  goToResultPage: PropTypes.func.isRequired,
  isExportEnabled: PropTypes.bool,
  openExportDialog: PropTypes.func.isRequired,

  // Heatmap specific
  open: PropTypes.bool,
  settings: PropTypes.object,
  fullScreenMode: PropTypes.bool,
  toggleFullScreenMode: PropTypes.func.isRequired,
  toggleNavigator: PropTypes.func.isRequired,
  toggleLegend: PropTypes.func.isRequired,
  showResultsList: PropTypes.func.isRequired,
  isLimitDisabled: PropTypes.bool,
  limitTo: PropTypes.func.isRequired,
  fetchHeatmapData: PropTypes.func.isRequired,
  heatmapData: PropTypes.object,
  matrixReceived: PropTypes.bool,
  onMatrixReceived: PropTypes.func.isRequired,
  onInitData: PropTypes.func.isRequired,
  onRequestData: PropTypes.func.isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  onUnmount: PropTypes.func.isRequired,
};

export const mapStateToProps = (state, ownProps) => {
  const { params } = ownProps;
  const relativeContexts = relativeContextItemsByHitSetKeySelector(state, params);
  return {
    relativeContexts,
    isExportEnabled: true,

    // Heatmap specific
    open: openSelector(state),
    settings: settingsSelector(state),
    fullScreenMode: fullScreenModeSelector(state),
    heatmapData: heatmapDataForParamsSelector(state, ownProps),
    matrixReceived: matrixReceivedForParamsSelector(state, ownProps),
    isLimitDisabled: isLimitDisabledSelector(state),
  };
};

const mapDispatchToProps = {
  getRelativeContexts: getRelativeContextsAction,
  selectDatabase: selectDatabaseAction,
  goToResultPage: goToResultPageAction,
  openExportDialog: openExportDialogAction,

  // Heatmap specific
  toggleFullScreenMode: toggleFullScreenModeAction,
  toggleNavigator: toggleNavigatorAction,
  toggleLegend: toggleLegendAction,
  showResultsList: showResultsListAction,
  limitTo: limitToAction,
  fetchHeatmapData: fetchHeatmapDataAction,
  onMatrixReceived: onMatrixReceivedAction,
  onInitData: onInitDataAction,
  onRequestData: onRequestDataAction,
  onSelectionChange: onSelectionChangeAction,
  onUnmount: onUnmountAction,
};

const EnhancedHeatmapContainer = compose(
  withCombinedRouteParamsforHeatmapResults,
  connect(mapStateToProps, mapDispatchToProps),
  withActiveBreadcrumb()
)(HeatmapContainer);

export default EnhancedHeatmapContainer;
